import {
  OcrExtractedPayslip,
  OcrPayslipJob,
  OcrPayslipJobCreated,
} from '../types/types';
import { normalizeFile } from '../utils/normalizeFile';
import { MS_PAYSLIP_ENDPOINT } from '../variables/GlobalVariables';
import request from './AxiosClient';

/**
 * Toutes les routes OCR sont scopées sous {@code /v2/payslip/ocr/{companyId}} pour rester
 * cohérentes avec le reste de l'API payslip (cf. {@code PayslipMonthController}).
 */

function ocrBase(companyId: string): string {
  return `${MS_PAYSLIP_ENDPOINT}/v2/payslip/ocr/${companyId}`;
}

/**
 * Lance un job OCR pour un BS (mono-fichier en v1 ; le splitter back gère le multi-BS interne).
 * Le back répond 202 + { jobId } et le worker @Scheduled traite en arrière-plan.
 */
function uploadBatch(
  companyId: string,
  file: File
): Promise<OcrPayslipJobCreated> {
  const normalized = normalizeFile(file);
  return request
    .post(
      `${ocrBase(companyId)}/upload`,
      { file: normalized, fileName: normalized.name },
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    .then((res) => res?.data);
}

function getJob(companyId: string, jobId: string): Promise<OcrPayslipJob> {
  return request
    .get(`${ocrBase(companyId)}/jobs/${jobId}`)
    .then((res) => res?.data);
}

function listJobs(companyId: string): Promise<OcrPayslipJob[]> {
  return request.get(`${ocrBase(companyId)}/jobs`).then((res) => res?.data);
}

/**
 * Persiste les corrections de l'écran review : remplace {@code items[idx].extractedFields}
 * du job. Le back répond avec le job mis à jour (lecture-after-write atomique).
 */
function updateItem(
  companyId: string,
  jobId: string,
  idx: number,
  corrected: OcrExtractedPayslip
): Promise<OcrPayslipJob> {
  return request
    .patch(`${ocrBase(companyId)}/jobs/${jobId}/items/${idx}`, corrected)
    .then((res) => res?.data);
}

/**
 * URL pour télécharger le PDF source d'un item (à charger via `request.get` avec
 * `responseType: 'arraybuffer'` puis `URL.createObjectURL` — le JWT doit être envoyé en header,
 * un iframe direct ne fonctionnerait pas).
 */
function getItemSourceUrl(
  companyId: string,
  jobId: string,
  idx: number
): string {
  return `${ocrBase(companyId)}/jobs/${jobId}/items/${idx}/source`;
}

/**
 * URL du flux SSE de progression d'un job OCR. À consommer via `useJobSse` (hook fetch-based
 * qui gère l'auth Bearer).
 */
function buildJobSseUrl(companyId: string, jobId: string): string {
  return `${ocrBase(companyId)}/jobs/${jobId}/sse`;
}

const PayslipOcrService = {
  uploadBatch,
  getJob,
  listJobs,
  updateItem,
  getItemSourceUrl,
  buildJobSseUrl,
};

export default PayslipOcrService;
